import { useEffect, useRef } from 'react';
import { Card } from '@ai-tutor/ui';
import { useLessonProgress } from '@/hooks/lesson/useLessonProgress';

interface LessonOptionsMenuProps {
  lessonId: string | undefined;
  onClose: () => void;
  /**
   * The button that opened the menu. A press on it is not an "outside" click
   * (the button's own toggle closes the menu), and Escape hands focus back to
   * it.
   */
  triggerRef?: React.RefObject<HTMLElement>;
}

const PART_LABELS: Record<string, string> = {
  text: 'Text',
  video: 'Video',
  podcast: 'Podcast',
  mindmap: 'Overview',
  highlights: 'Highlights',
  notes: 'Notes',
  practice: 'Practice',
  reference: 'Reference',
};

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  all_parts_engaged: 'All parts engaged',
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

/**
 * Lesson three-dot menu - shows live Coverage, 3-state status, per-part
 * time, and the Engagement strip from GET /lessons/{id}/progress.
 *
 * Coverage = engaged_parts / total_present_parts. A part is "engaged"
 * when its cumulative ContentActivity duration crosses 30s. Mastery is
 * NOT shown here - it's a concept-level concept that lives in Mindspace.
 */
export function LessonOptionsMenu({
  lessonId,
  onClose,
  triggerRef,
}: LessonOptionsMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // A press on the trigger is left to the trigger. Closing here too made
      // the button's own click toggle the menu straight back OPEN - clicking
      // ⋯ again never closed it (`Lesson/Viewer/ChecksTheLessonProgress`).
      if (triggerRef?.current?.contains(target)) return;
      if (containerRef.current && !containerRef.current.contains(target)) {
        onClose();
      }
    };
    // Escape closes, and focus goes back to the button - a keyboard user had
    // no way out of the menu but tabbing past it.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onClose();
      triggerRef?.current?.focus();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, triggerRef]);

  return (
    <>
      <div className='fixed inset-0 z-10' />

      <Card
        ref={containerRef}
        className='animate-scale-in absolute right-0 top-10 z-20 w-72 border bg-card shadow-lg sm:w-80'
      >
        <LessonProgressContent lessonId={lessonId} />
      </Card>
    </>
  );
}

/**
 * The menu's body - Coverage, status, time per part - on its own, so the
 * phone can show it in a bottom sheet (`LessonTopBar`) and the popover above
 * can wrap it.
 */
export function LessonProgressContent({
  lessonId,
}: {
  lessonId: string | undefined;
}) {
  const { data, isLoading, isError } = useLessonProgress(lessonId);
  const coveragePct = data ? Math.round(data.coverage * 100) : 0;

  return (
    <div className='p-4'>
      <div className='mb-3 flex items-center justify-between'>
        <h4 className='text-sm font-semibold text-foreground'>Progress</h4>
        {data && (
          <span className='rounded-full bg-muted px-2 py-0.5 text-xs text-foreground'>
            {STATUS_LABELS[data.status] ?? data.status}
          </span>
        )}
      </div>

      {isLoading && (
        <div className='space-y-2'>
          <div className='h-2 w-full animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          <div className='h-2 w-2/3 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
          <div className='h-2 w-1/2 animate-pulse rounded-sm bg-secondary dark:bg-white/10' />
        </div>
      )}

      {isError && (
        <p className='text-xs text-muted-foreground'>
          Couldn't load progress. Try again in a moment.
        </p>
      )}

      {data && (
        <div className='space-y-3'>
          {/* Coverage headline */}
          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <span className='text-sm font-medium text-foreground'>
                Coverage
              </span>
              {/* brand-700, not 500: 500 on the white card fails WCAG AA
                  (axe, `Lesson/Viewer/ChecksTheLessonProgress`). */}
              <span className='text-sm font-semibold text-brand-700 dark:text-primary'>
                {coveragePct}%
              </span>
            </div>
            <div className='h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-white/10'>
              <div
                className='h-full rounded-full bg-brand-500 transition-all duration-500 dark:bg-primary'
                style={{ width: `${coveragePct}%` }}
              />
            </div>
            <p className='text-xs text-muted-foreground'>
              {data.engaged_parts} of {data.total_present_parts} parts engaged
            </p>
          </div>

          {/* Per-part time breakdown */}
          {data.per_part.length > 0 && (
            <div className='space-y-1.5 border-t border-border pt-2'>
              {data.per_part.map(p => (
                <div
                  key={p.part}
                  className='flex items-center justify-between text-xs'
                >
                  <span
                    className={
                      p.engaged ? 'text-foreground' : 'text-muted-foreground'
                    }
                  >
                    {p.engaged ? '✓ ' : '  '}
                    {PART_LABELS[p.part] ?? p.part}
                  </span>
                  <span className='text-muted-foreground'>
                    {formatDuration(p.duration_s)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Engagement strip */}
          <div className='flex items-center justify-between border-t border-border pt-2 text-xs text-muted-foreground'>
            <span>{data.engagement.notes} notes</span>
            <span>·</span>
            <span>{data.engagement.sources} sources</span>
            <span>·</span>
            <span>{data.engagement.doubts} doubts</span>
          </div>
        </div>
      )}
    </div>
  );
}
